import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "../../sidebar";
import {
  MainContainer,
  BackgroundPattern,
  SideContainer,
  ContentWrapper,
  HeaderSection,
  Title,
  ButtonRow,
  ButtonContainerLeft,
  StyledButton,
} from "../ListAreas/styles";
import {
  FormCard,
  FormRow,
  Label,
  Input,
  Hint,
  SubmitButton,
  SecondaryLink,
  Feedback,
  OutcomeBadge,
  MetaList,
  PageIntro,
  EmailStatusBanner,
} from "./styles";
import {
  guardianInviteService,
  INVITE_OUTCOME_LABELS,
} from "../../../services/guardianInviteService";
import { goToGuardianHub } from "../../../utils/appNavigation";
import { appStatus } from "../../../store/modules/app_status/actions";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function resolveEmailDelivery(result) {
  if (!result || typeof result !== "object") {
    return { status: "unknown", title: "Status do e-mail não informado", detail: null };
  }

  if (result.email_sent === true) {
    return {
      status: "sent",
      title: "E-mail de boas-vindas enviado com sucesso",
      detail: result.email_error
        ? null
        : "Confirmado pelo backend (SMTP).",
    };
  }

  if (result.email_sent === false) {
    return {
      status: "failed",
      title: "E-mail de boas-vindas NÃO foi enviado",
      detail:
        result.email_error ||
        result.email_send_error ||
        "O cadastro/identificação pode ter ocorrido, mas o envio SMTP falhou ou não foi confirmado.",
    };
  }

  return {
    status: "unknown",
    title: "Não foi possível confirmar se o e-mail foi enviado",
    detail:
      "A API não retornou o campo email_sent. Peça ao backend para informar true/false de forma verídica após o SMTP.",
  };
}

export default function InviteWelcomePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const collapsed = useSelector((state) => state.sidebar);

  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setResult(null);
    setError(null);

    const normalized = email.trim().toLowerCase();
    if (!normalized || !EMAIL_RE.test(normalized)) {
      setError("Informe um e-mail válido.");
      return;
    }

    setSending(true);
    try {
      const data = await guardianInviteService.sendInviteWelcome(normalized);
      setResult(data);
      setEmail("");
    } catch (err) {
      const status = err?.response?.status;
      const apiMsg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.response?.data?.message;

      if (status === 400) {
        setError(apiMsg || "E-mail ausente ou inválido.");
      } else if (status === 401) {
        setError("Sessão expirada. Faça login novamente.");
      } else {
        setError(apiMsg || "Falha ao enviar o convite. Tente novamente.");
      }
    } finally {
      setSending(false);
    }
  };

  const goToFollowUp = () => {
    if (!result) return;

    if (result.outcome === "created_user_request") {
      dispatch(appStatus("Guardião"));
      navigate("/user_request");
      return;
    }

    dispatch(appStatus("Usuários"));
    navigate("/users", {
      state: { filterEmail: result.email },
    });
  };

  const outcomeLabel =
    INVITE_OUTCOME_LABELS[result?.outcome] || result?.outcome || "—";
  const emailDelivery = resolveEmailDelivery(result);
  const feedbackVariant =
    emailDelivery.status === "sent"
      ? "success"
      : emailDelivery.status === "failed"
        ? "warning"
        : result?.success
          ? "warning"
          : "success";

  return (
    <MainContainer collapsed={collapsed}>
      <BackgroundPattern />
      <Sidebar />
      <SideContainer collapsed={collapsed}>
        <ContentWrapper>
          <ButtonRow>
            <ButtonContainerLeft>
              <StyledButton
                type="button"
                onClick={() => goToGuardianHub(dispatch, navigate)}
              >
                Voltar
              </StyledButton>
            </ButtonContainerLeft>
          </ButtonRow>

          <HeaderSection>
            <Title>Enviar boas-vindas Guardian</Title>
            <PageIntro>
              Use essa seção para enviar um e-mail de boas-vindas para um Guardian.
              O usuário recebe um e-mail com o link da aplicação para download na
              loja (atualmente disponível apenas para Android). Além disso, ele
              também recebe um manual em PDF de como utilizar a aplicação. O
              usuário não cadastrado no sistema recebe juntamente uma senha e um
              pré-cadastro é realizado no sistema para que ele continue. Caso o
              usuário já exista no sistema, será identificado.
            </PageIntro>
          </HeaderSection>

          <FormCard onSubmit={handleSubmit}>
            <FormRow>
              <Label htmlFor="invite-email">E-mail do destinatário</Label>
              <Input
                id="invite-email"
                type="email"
                autoComplete="email"
                placeholder="pessoa@dominio.com"
                value={email}
                disabled={sending}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Hint>
                Se o usuário não existir, um Guardian será criado só com o e-mail
                (senha temporária no e-mail).
              </Hint>
            </FormRow>

            <SubmitButton type="submit" disabled={sending}>
              {sending ? "Enviando…" : "Enviar boas-vindas"}
            </SubmitButton>

            {error ? (
              <Feedback $variant="error" role="alert">
                {error}
              </Feedback>
            ) : null}

            {result?.success ? (
              <Feedback $variant={feedbackVariant} role="status">
                <EmailStatusBanner $status={emailDelivery.status}>
                  {emailDelivery.title}
                  {emailDelivery.detail ? <span>{emailDelivery.detail}</span> : null}
                </EmailStatusBanner>
                <div>
                  <OutcomeBadge $outcome={result.outcome}>
                    {outcomeLabel}
                  </OutcomeBadge>
                  {result.how_to_find?.hint ||
                    result.message ||
                    "Convite processado com sucesso."}
                </div>
                <MetaList>
                  <li>E-mail: {result.email}</li>
                  <li>Tabela: {result.table || "—"}</li>
                  <li>
                    Senha no e-mail:{" "}
                    {result.password_sent === true
                      ? "sim"
                      : result.password_sent === false
                        ? "não"
                        : "não informado"}
                  </li>
                  <li>
                    E-mail enviado:{" "}
                    {result.email_sent === true
                      ? "sim"
                      : result.email_sent === false
                        ? "não"
                        : "não informado pela API"}
                  </li>
                  <li>
                    Áreas no e-mail:{" "}
                    {Array.isArray(result.areas) ? result.areas.length : 0}
                  </li>
                  <li>
                    Links das lojas:{" "}
                    {result.store_links ? "incluídos" : "nenhum cadastrado"}
                  </li>
                </MetaList>
                <SecondaryLink type="button" onClick={goToFollowUp}>
                  {result.outcome === "created_user_request"
                    ? "Abrir solicitações de cadastro →"
                    : "Abrir usuários →"}
                </SecondaryLink>
              </Feedback>
            ) : null}
          </FormCard>
        </ContentWrapper>
      </SideContainer>
    </MainContainer>
  );
}